/**
 * Zone-less calendar arithmetic for the date picker. Calendar dates ("YYYY-MM-DD") have no time
 * zone, so plain-date math here is safe; anything involving instants goes through src/lib/time.
 */
import { Temporal } from 'temporal-polyfill';
import { formatPlainDate, isPlainDate } from '@/lib/time';

export type DateOrderPart = 'year' | 'month' | 'day';

export function addDays(date: string, days: number): string {
  return Temporal.PlainDate.from(date).add({ days }).toString();
}

export function addMonths(date: string, months: number): string {
  return Temporal.PlainDate.from(date).add({ months }).toString();
}

export function compareDates(a: string, b: string): number {
  return Temporal.PlainDate.compare(Temporal.PlainDate.from(a), Temporal.PlainDate.from(b));
}

export function clampDate(date: string, min?: string, max?: string): string {
  if (min && isPlainDate(min) && compareDates(date, min) < 0) return min;
  if (max && isPlainDate(max) && compareDates(date, max) > 0) return max;
  return date;
}

export function isOutOfRange(date: string, min?: string, max?: string): boolean {
  return Boolean(
    (min && isPlainDate(min) && compareDates(date, min) < 0) ||
    (max && isPlainDate(max) && compareDates(date, max) > 0),
  );
}

/** First day of the month containing `date`. */
export function startOfMonth(date: string): string {
  return Temporal.PlainDate.from(date).with({ day: 1 }).toString();
}

/** Start of the week containing `date`. weekStartsOn: 0 = Sunday, 1 = Monday. */
export function startOfWeek(date: string, weekStartsOn: 0 | 1): string {
  const d = Temporal.PlainDate.from(date);
  // Temporal dayOfWeek: Monday = 1 ... Sunday = 7.
  const index = weekStartsOn === 0 ? d.dayOfWeek % 7 : d.dayOfWeek - 1;
  return d.subtract({ days: index }).toString();
}

export function endOfWeek(date: string, weekStartsOn: 0 | 1): string {
  return addDays(startOfWeek(date, weekStartsOn), 6);
}

/** 6 weeks x 7 days covering the month of `monthDate`. */
export function monthGrid(monthDate: string, weekStartsOn: 0 | 1): string[][] {
  const first = startOfWeek(startOfMonth(monthDate), weekStartsOn);
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(first, week * 7 + day)),
  );
}

export function sameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

export function monthLabel(date: string, locale?: string): string {
  return Temporal.PlainDate.from(date).toLocaleString(locale, { month: 'long', year: 'numeric' });
}

export function weekdayLabels(
  weekStartsOn: 0 | 1,
  locale?: string,
): { short: string; long: string }[] {
  // 2023-01-01 was a Sunday.
  const sunday = Temporal.PlainDate.from('2023-01-01');
  return Array.from({ length: 7 }, (_, i) => {
    const d = sunday.add({ days: i + weekStartsOn });
    return {
      short: d.toLocaleString(locale, { weekday: 'short' }),
      long: d.toLocaleString(locale, { weekday: 'long' }),
    };
  });
}

export function fullDateLabel(date: string, locale?: string): string {
  return formatPlainDate(date, { locale, style: 'full' });
}

export function dayOfMonth(date: string): number {
  return Temporal.PlainDate.from(date).day;
}

/** Locale's numeric date field order and separator, e.g. month/day/year with "/" for en-US. */
export function dateInputFormat(locale?: string): { order: DateOrderPart[]; separator: string } {
  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  }).formatToParts(new Date(Date.UTC(2020, 10, 22)));
  const order = parts
    .filter(
      (p): p is Intl.DateTimeFormatPart & { type: DateOrderPart } =>
        p.type === 'year' || p.type === 'month' || p.type === 'day',
    )
    .map((p) => p.type);
  const separator = parts.find((p) => p.type === 'literal')?.value.trim() || '/';
  return {
    order: order.length === 3 ? order : ['month', 'day', 'year'],
    separator,
  };
}

export function datePlaceholder(locale?: string): string {
  const { order, separator } = dateInputFormat(locale);
  const token: Record<DateOrderPart, string> = { year: 'yyyy', month: 'mm', day: 'dd' };
  return order.map((p) => token[p]).join(separator);
}

/** Formats a plain date for the text input using the locale's numeric order. */
export function formatDateInput(date: string, locale?: string): string {
  if (!date || !isPlainDate(date)) return date;
  const { order, separator } = dateInputFormat(locale);
  const [year, month, day] = date.split('-');
  const values: Record<DateOrderPart, string> = {
    year: year ?? '',
    month: month ?? '',
    day: day ?? '',
  };
  return order.map((p) => values[p]).join(separator);
}

/**
 * Parses typed text into "YYYY-MM-DD". Accepts ISO ("2026-03-08") and the locale's numeric order
 * with any separator ("3/8/2026", "03-08-2026"). Returns null when invalid.
 */
export function parseDateInput(text: string, locale?: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-');
    return toIso(Number(y), Number(m), Number(d));
  }
  const pieces = trimmed.split(/[^\d]+/).filter(Boolean);
  if (pieces.length !== 3) return null;
  const { order } = dateInputFormat(locale);
  const values: Partial<Record<DateOrderPart, number>> = {};
  order.forEach((part, index) => {
    values[part] = Number(pieces[index]);
  });
  let year = values.year ?? NaN;
  const yearText = pieces[order.indexOf('year')] ?? '';
  if (yearText.length === 2) year += 2000;
  else if (yearText.length !== 4) return null;
  return toIso(year, values.month ?? NaN, values.day ?? NaN);
}

function toIso(year: number, month: number, day: number): string | null {
  if (![year, month, day].every(Number.isFinite)) return null;
  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return isPlainDate(iso) ? iso : null;
}
